import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import { CalendarClock, CheckSquare, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import LeiderschapLayout from '@/components/LeiderschapLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { leiderschapApi } from '../api';
import SectionHeader from '../components/SectionHeader';
import ConfidentialBadge from '../components/ConfidentialBadge';
import type { ActionItem, LeadershipMeeting } from '../types';

export default function OverviewPage() {
  const { user } = useAuth();
  const [meetings, setMeetings] = useState<LeadershipMeeting[]>([]);
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [meetingsRes, actionItemsRes] = await Promise.all([
          leiderschapApi.listMeetings(),
          leiderschapApi.listActionItems(),
        ]);
        if (!active) return;
        setMeetings(meetingsRes);
        setActionItems(actionItemsRes);
      } catch (error) {
        toast.error('Kon overzicht niet laden');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const upcomingMeetings = useMemo(
    () =>
      meetings
        .filter((m) => m.status === 'gepland' && new Date(m.scheduled_at).getTime() >= Date.now())
        .sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime())
        .slice(0, 5),
    [meetings],
  );

  const myOpenActionItems = useMemo(
    () =>
      actionItems
        .filter((item) => item.assignee_user_id === user?.id && (item.status === 'open' || item.status === 'in_uitvoering'))
        .sort((a, b) => (a.due_date || '9999').localeCompare(b.due_date || '9999')),
    [actionItems, user?.id],
  );

  const openActionItemCount = useMemo(
    () => actionItems.filter((item) => item.status === 'open' || item.status === 'in_uitvoering').length,
    [actionItems],
  );

  return (
    <LeiderschapLayout>
      <SectionHeader title="Overzicht" description="Je persoonlijke overzicht van aankomende vergaderingen en actiepunten." />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-6">
        <Card>
          <CardContent className="p-5 flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-100 text-teal-700">
              <CalendarClock className="h-5 w-5" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{upcomingMeetings.length}</p>
              <p className="text-sm text-slate-500">Aankomende vergaderingen</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5 flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <ClipboardList className="h-5 w-5" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{openActionItemCount}</p>
              <p className="text-sm text-slate-500">Openstaande actiepunten</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5 flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
              <CheckSquare className="h-5 w-5" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{myOpenActionItems.length}</p>
              <p className="text-sm text-slate-500">Mijn openstaande actiepunten</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Aankomende vergaderingen</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {!loading && upcomingMeetings.length === 0 && <p className="text-sm text-slate-500">Geen geplande vergaderingen.</p>}
            {upcomingMeetings.map((meeting) => (
              <Link
                key={meeting.id}
                to={`/leiderschap/vergaderingen/${meeting.id}`}
                className="flex items-center justify-between rounded-lg border border-slate-100 p-3 hover:bg-slate-50"
              >
                <div>
                  <p className="text-sm font-medium text-slate-900">{meeting.title}</p>
                  <p className="text-xs text-slate-500">
                    {format(parseISO(meeting.scheduled_at), "d MMMM yyyy 'om' HH:mm", { locale: nl })}
                  </p>
                </div>
                <ConfidentialBadge confidentiality={meeting.confidentiality} />
              </Link>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Mijn actiepunten</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {!loading && myOpenActionItems.length === 0 && <p className="text-sm text-slate-500">Geen openstaande actiepunten.</p>}
            {myOpenActionItems.map((item) => (
              <Link
                key={item.id}
                to="/leiderschap/actiepunten"
                className="flex items-center justify-between rounded-lg border border-slate-100 p-3 hover:bg-slate-50"
              >
                <p className="text-sm font-medium text-slate-900">{item.title}</p>
                {item.due_date && (
                  <p className="text-xs text-slate-500">{format(parseISO(item.due_date), 'd MMM yyyy', { locale: nl })}</p>
                )}
              </Link>
            ))}
          </CardContent>
        </Card>
      </div>
    </LeiderschapLayout>
  );
}
